import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  onSnapshot,
  arrayUnion,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../config/firebase";
import { isAllowedEmail } from "../config/allowedUsers";
import * as problemApi from "../services/problemApi";
import { normalizeProblemType } from "../utils/problemType";
import {
  ATTEMPT_FSM_VERSION,
  AttemptEventTypes,
  AttemptStates,
  applyAttemptEvent,
  createAttemptSnapshot,
} from "../fsm/problemAttemptFsm";
import { HINT_LEVELS } from "../data/hintResources";
import {
  appendTelemetryEvent,
  createProblemAttempt,
  isTelemetryEnabled as isTelemetryEnabledHelper,
  updateProblemAttempt,
} from "../services/telemetryStore";
import {
  createFlashcardRecord,
  fetchDueFlashcards,
  fetchFlashcards,
  deleteFlashcardRecord,
  updateFlashcardRecord,
} from "../services/flashcardStore";
import { createFsrsState, scheduleFsrsReview } from "../utils/fsrs";
import { selectDueFlashcards } from "../utils/flashcards";

const TaskContext = createContext(null);

const parseListId = (id) => {
  if (!id) return Number.MIN_SAFE_INTEGER;
  const parts = id.split("-").map((part) => Number(part));
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day).getTime();
  }

  return Number.MIN_SAFE_INTEGER;
};

const sortTasks = (tasks) =>
  [...tasks].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number") {
      return a.order - b.order;
    }

    if (typeof a.order === "number") return -1;
    if (typeof b.order === "number") return 1;

    return (a.task || "").localeCompare(b.task || "");
  });

const pad = (value) => String(value).padStart(2, "0");

const normalizeDateId = (input) => {
  if (!input) return null;
  if (input instanceof Date) {
    return `${input.getFullYear()}-${pad(input.getMonth() + 1)}-${pad(
      input.getDate()
    )}`;
  }

  if (typeof input === "string") {
    const parts = input.trim().split("-");
    if (parts.length === 3) {
      const [rawYear, rawMonth, rawDay] = parts;
      const year = Number(rawYear);
      const month = Number(rawMonth);
      const day = Number(rawDay);
      if (
        Number.isInteger(year) &&
        Number.isInteger(month) &&
        Number.isInteger(day)
      ) {
        return `${year}-${pad(month)}-${pad(day)}`;
      }
    }
  }

  return null;
};

const isValidDateId = (value) => {
  const normalized = normalizeDateId(value);
  if (!normalized) return false;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return false;
  return normalizeDateId(date) === normalized;
};

const formatDateId = (value = new Date()) => normalizeDateId(value);

export const TaskProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [todos, setTodos] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDateState] = useState(() =>
    formatDateId(new Date())
  );
  const [listSummaries, setListSummaries] = useState({});
  const [problems, setProblems] = useState([]);
  const hintStreamControllersRef = useRef(new Map());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && !isAllowedEmail(firebaseUser.email)) {
        signOut(auth).catch(() => {});
        setUser(null);
        return;
      }

      setUser(firebaseUser);

      if (!firebaseUser) {
        setUserProfile(null);
        setLists([]);
        setActiveListId(null);
        setTodos([]);
        setCompletedTodos([]);
        setProblems([]);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return undefined;
    }

    const profileRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      profileRef,
      (snapshot) => {
        const data = snapshot.data();
        if (data?.userInfo) {
          setUserProfile(data.userInfo);
          return;
        }
        setUserProfile({
          name: user.displayName || "",
          email: user.email || "",
          photoURL: user.photoURL || null,
        });
      },
      (error) => {
        console.error("Failed to load user profile", error);
      }
    );

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    const listsRef = collection(db, `users/${user.uid}/todoLists`);

    const unsubscribe = onSnapshot(listsRef, (snapshot) => {
      const listDocs = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .sort((a, b) => parseListId(b.id) - parseListId(a.id));

      setLists(listDocs);

      const desiredDate = normalizeDateId(selectedDate);
      if (desiredDate && isValidDateId(desiredDate)) {
        const selectedExists = listDocs.some((list) => list.id === desiredDate);
        setActiveListId((current) =>
          current === desiredDate ? current : desiredDate
        );
        if (!selectedExists) {
          void setDoc(doc(db, `users/${user.uid}/todoLists/${desiredDate}`), {
            createdAt: Date.now(),
          }).catch((error) => {
            console.error("Failed to create list", error);
          });
        }
        return;
      }

      if (!listDocs.length) {
        setActiveListId(null);
        return;
      }

      const hasActiveList =
        activeListId && listDocs.some((list) => list.id === activeListId);

      if (!hasActiveList) {
        setActiveListId(listDocs[0].id);
      }
    });

    return unsubscribe;
  }, [user, activeListId, selectedDate]);

  useEffect(() => {
    if (!user || !activeListId) {
      setTodos([]);
      setCompletedTodos([]);
      setProblems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const basePath = `users/${user.uid}/todoLists/${activeListId}`;
    const todosRef = collection(db, `${basePath}/todos`);
    const completedTodosRef = collection(db, `${basePath}/completedTodos`);

    const unsubscribeTodos = onSnapshot(todosRef, (snapshot) => {
      const nextTodos = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setTodos(sortTasks(nextTodos));
      setLoading(false);
    });

    const unsubscribeCompleted = onSnapshot(completedTodosRef, (snapshot) => {
      const nextCompleted = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setCompletedTodos(sortTasks(nextCompleted));
    });

    return () => {
      unsubscribeTodos();
      unsubscribeCompleted();
    };
  }, [user, activeListId]);

  useEffect(() => {
    if (!user || !activeListId) {
      setProblems([]);
      return undefined;
    }

    const problemsRef = collection(
      db,
      `users/${user.uid}/todoLists/${activeListId}/problems`
    );

    const unsubscribe = onSnapshot(problemsRef, (snapshot) => {
      const nextProblems = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const normalizedTimestamp =
          data?.timestamp?.toMillis?.() ?? data?.timestamp ?? null;
        return {
          id: docSnap.id,
          ...data,
          timestamp: normalizedTimestamp,
        };
      });
      setProblems(nextProblems);
    });

    return unsubscribe;
  }, [user, activeListId]);

  useEffect(() => {
    if (activeListId && isValidDateId(activeListId)) {
      setSelectedDateState(normalizeDateId(activeListId));
    }
  }, [activeListId]);

  useEffect(() => {
    if (!activeListId) return;
    setListSummaries((prev) => ({
      ...prev,
      [activeListId]: {
        todos: todos.length,
        completed: completedTodos.length,
      },
    }));
  }, [activeListId, todos.length, completedTodos.length]);

  useEffect(() => {
    if (!user) {
      setListSummaries({});
      return;
    }

    if (!lists.length) {
      setListSummaries({});
      return;
    }

    let cancelled = false;

    const fetchSummaries = async () => {
      try {
        const summaries = await Promise.all(
          lists.map(async (list) => {
            const todosRef = collection(
              db,
              `users/${user.uid}/todoLists/${list.id}/todos`
            );
            const completedRef = collection(
              db,
              `users/${user.uid}/todoLists/${list.id}/completedTodos`
            );

            const [todosCountSnap, completedCountSnap] = await Promise.all([
              getCountFromServer(todosRef),
              getCountFromServer(completedRef),
            ]);

            return [list.id, {
              todos: todosCountSnap.data().count,
              completed: completedCountSnap.data().count,
            }];
          })
        );

        if (!cancelled) {
          setListSummaries(Object.fromEntries(summaries));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load list summaries", error);
        }
      }
    };

    fetchSummaries();

    return () => {
      cancelled = true;
    };
  }, [user, lists]);

  const ensureUser = useCallback(() => {
    if (!user) {
      throw new Error("You need to be signed in to manage tasks.");
    }
  }, [user]);

  const ensureListReady = useCallback(() => {
    ensureUser();
    if (!activeListId) {
      throw new Error("Select or create a list before managing tasks.");
    }
  }, [activeListId, ensureUser]);

  const telemetryEnabled = isTelemetryEnabledHelper();

  const logTelemetryEvent = useCallback(
    async (event, nowMs = Date.now()) => {
      if (!event) return null;
      if (!user?.uid || !activeListId) return null;
      try {
        return await appendTelemetryEvent({
          db,
          userId: user.uid,
          listId: activeListId,
          event,
          nowMs,
        });
      } catch (error) {
        console.warn("Failed to persist telemetry event", error);
        return null;
      }
    },
    [activeListId, user?.uid]
  );

  const createFlashcard = useCallback(
    async (payload = {}) => {
      ensureUser();
      const front = String(payload.front || "").trim();
      const back = String(payload.back || "").trim();
      if (!front || !back) return null;

      const nowMs = Date.now();
      const fsrs = createFsrsState({ nowMs });
      const dueAt =
        typeof payload.dueAt === "number" && Number.isFinite(payload.dueAt)
          ? payload.dueAt
          : nowMs;
      const tags = Array.isArray(payload.tags)
        ? payload.tags.map((tag) => String(tag)).filter(Boolean)
        : [];
      const deckName = String(payload.deckName || payload?.anki?.deckName || "Obey The Bell").trim() || "Obey The Bell";
      const noteType = String(payload.noteType || payload?.anki?.noteType || "Basic").trim() || "Basic";

      const record = {
        front,
        back,
        type: payload.type || "basic",
        tags,
        origin: payload.origin || "manual",
        anki: {
          deckName,
          noteType,
          fields: {
            Front: front,
            Back: back,
          },
          tags,
        },
        createdAt: nowMs,
        updatedAt: nowMs,
        dueAt,
        fsrs,
        userId: user.uid,
      };

      if (activeListId) {
        record.listId = activeListId;
      }
      if (payload.sourceProblemId) {
        record.sourceProblemId = payload.sourceProblemId;
      }
      if (payload.sourceAttemptId) {
        record.sourceAttemptId = payload.sourceAttemptId;
      }
      if (payload.problemType) {
        record.problemType = payload.problemType;
      }

      const created = await createFlashcardRecord({
        db,
        userId: user.uid,
        payload: record,
      });

      return { id: created.id, ...record };
    },
    [activeListId, ensureUser, user?.uid]
  );

  const getDueFlashcards = useCallback(
    async ({ limit: limitCount = 5, nowMs = Date.now(), minDue = 2 } = {}) => {
      ensureUser();
      const cards = await fetchDueFlashcards({
        db,
        userId: user.uid,
        nowMs,
        limitCount,
      });
      const normalized = cards.map((card) => {
        const dueAt =
          typeof card?.dueAt?.toMillis === "function"
            ? card.dueAt.toMillis()
            : card.dueAt;
        const createdAt =
          typeof card?.createdAt?.toMillis === "function"
            ? card.createdAt.toMillis()
            : card.createdAt;
        const updatedAt =
          typeof card?.updatedAt?.toMillis === "function"
            ? card.updatedAt.toMillis()
            : card.updatedAt;
        const lastReviewedAt =
          typeof card?.lastReviewedAt?.toMillis === "function"
            ? card.lastReviewedAt.toMillis()
            : card.lastReviewedAt;
        return {
          ...card,
          dueAt,
          createdAt,
          updatedAt,
          lastReviewedAt,
        };
      });

      return selectDueFlashcards(normalized, {
        nowMs,
        limit: limitCount,
        minDue,
      });
    },
    [ensureUser, user?.uid]
  );

  const reviewFlashcard = useCallback(
    async (card, rating) => {
      ensureUser();
      if (!card) return null;
      const cardId = card.id || card.cardId;
      if (!cardId) return null;
      const nowMs = Date.now();
      const schedule = scheduleFsrsReview({
        card,
        rating,
        nowMs,
      });
      const updates = {
        dueAt: schedule.dueAt,
        intervalDays: schedule.intervalDays,
        lastReviewedAt: nowMs,
        lastRating: schedule.ratingValue,
        lastRatingLabel: schedule.ratingLabel,
        fsrs: schedule.fsrs,
        updatedAt: nowMs,
      };

      await updateFlashcardRecord({
        db,
        userId: user.uid,
        cardId,
        updates,
      });

      return { ...card, ...updates };
    },
    [ensureUser, user?.uid]
  );

  const normalizeFlashcardTimestamps = useCallback((card) => {
    if (!card) return card;
    const normalize = (value) =>
      typeof value?.toMillis === "function" ? value.toMillis() : value;
    return {
      ...card,
      dueAt: normalize(card.dueAt),
      createdAt: normalize(card.createdAt),
      updatedAt: normalize(card.updatedAt),
      lastReviewedAt: normalize(card.lastReviewedAt),
    };
  }, []);

  const getFlashcards = useCallback(
    async ({
      limit: limitCount = 200,
      orderField = "updatedAt",
      orderDirection = "desc",
    } = {}) => {
      ensureUser();
      const cards = await fetchFlashcards({
        db,
        userId: user.uid,
        limitCount,
        orderField,
        orderDirection,
      });
      return cards.map((card) => normalizeFlashcardTimestamps(card));
    },
    [ensureUser, normalizeFlashcardTimestamps, user?.uid]
  );

  const updateFlashcard = useCallback(
    async (card, updates = {}) => {
      ensureUser();
      const cardId = card?.id || card?.cardId;
      if (!cardId) return null;

      const nowMs = Date.now();
      const nextFront =
        typeof updates.front === "string"
          ? updates.front.trim()
          : String(card?.front || "").trim();
      const nextBack =
        typeof updates.back === "string"
          ? updates.back.trim()
          : String(card?.back || "").trim();
      const nextTags = Array.isArray(updates.tags)
        ? updates.tags.map((tag) => String(tag)).filter(Boolean)
        : Array.isArray(card?.tags)
        ? card.tags.map((tag) => String(tag)).filter(Boolean)
        : [];
      const nextDeckName = String(
        updates.deckName ||
          updates?.anki?.deckName ||
          card?.anki?.deckName ||
          "Obey The Bell"
      ).trim();
      const nextNoteType = String(
        updates.noteType ||
          updates?.anki?.noteType ||
          card?.anki?.noteType ||
          "Basic"
      ).trim();

      const payload = {
        ...updates,
        front: nextFront,
        back: nextBack,
        tags: nextTags,
        updatedAt: nowMs,
        anki: {
          ...(card?.anki || {}),
          ...(updates?.anki || {}),
          deckName: nextDeckName || "Obey The Bell",
          noteType: nextNoteType || "Basic",
          fields: {
            Front: nextFront,
            Back: nextBack,
          },
          tags: nextTags,
        },
      };

      delete payload.id;
      delete payload.cardId;

      await updateFlashcardRecord({
        db,
        userId: user.uid,
        cardId,
        updates: payload,
      });

      return normalizeFlashcardTimestamps({ ...card, ...payload });
    },
    [ensureUser, normalizeFlashcardTimestamps, user?.uid]
  );

  const deleteFlashcard = useCallback(
    async (cardId) => {
      ensureUser();
      const id = String(cardId || "").trim();
      if (!id) return;
      await deleteFlashcardRecord({
        db,
        userId: user.uid,
        cardId: id,
      });
    },
    [ensureUser, user?.uid]
  );

  const getCollectionRef = useCallback(
    (collectionName) => {
      ensureListReady();
      return collection(
        db,
        `users/${user.uid}/todoLists/${activeListId}/${collectionName}`
      );
    },
    [activeListId, ensureListReady, user]
  );

  const getDocRef = useCallback(
    (collectionName, id) =>
      doc(
        db,
        `users/${user?.uid}/todoLists/${activeListId}/${collectionName}/${id}`
      ),
    [activeListId, user?.uid]
  );

  const uploadFileToStorage = useCallback(
    async (file, pathHint) => {
      if (!file) return null;
      const safeName = file.name?.replace?.(/\s+/g, "_") || "upload";
      const storagePath =
        pathHint ||
        `users/${user?.uid}/todoLists/${activeListId}/problems/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    },
    [activeListId, user?.uid]
  );

  const writeTodosWithRunner = useCallback(
    async (nextTodos) => {
      if (!nextTodos.length) return;
      const runningTodo = nextTodos.find((todo) => todo.isRunning);
      const runningId = runningTodo?.id ?? null;

      const batch = writeBatch(db);
      nextTodos.forEach((task, index) => {
        const payload = {
          order: index,
          isRunning: runningId ? task.id === runningId : false,
        };
        batch.update(getDocRef("todos", task.id), payload);
      });

      await batch.commit();
    },
    [getDocRef]
  );

  const addTask = useCallback(
    async ({
      task,
      primaryDuration = 25 * 60,
      secondaryDuration = 5 * 60,
      numCycles = 1,
      tilDone = false,
      column = "column-1",
      isSpecial = false,
      isProblemSolving = false,
      hintDelaysSec = null,
    }) => {
      ensureListReady();
      const normalizedHintDelays =
        Array.isArray(hintDelaysSec) &&
        hintDelaysSec.length === 5 &&
        hintDelaysSec.every((value) => Number.isFinite(value) && value >= 0)
          ? hintDelaysSec.map((value) => Math.max(0, Math.round(value)))
          : null;
      const payload = {
        task,
        primaryDuration,
        secondaryDuration,
        numCycles,
        tilDone,
        column,
        isSpecial,
        complete: false,
        completed: false,
        isRunning: false,
        userId: user.uid,
        order: todos.length,
        totalElapsedTime: 0,
        isProblemSolving,
        ...(normalizedHintDelays ? { hintDelaysSec: normalizedHintDelays } : {}),
        activeProblemId: null,
      };

      const docRef = await addDoc(getCollectionRef("todos"), payload);
      return docRef.id;
    },
    [ensureListReady, getCollectionRef, todos.length, user?.uid]
  );

  const updateTask = useCallback(
    async (id, updates, collectionHint) => {
      ensureListReady();

      const isInTodos =
        collectionHint === "todos"
          ? true
          : collectionHint === "completedTodos"
          ? false
          : todos.some((todo) => todo.id === id);

      const collectionName = isInTodos ? "todos" : "completedTodos";
      await updateDoc(getDocRef(collectionName, id), updates);
    },
    [ensureListReady, getDocRef, todos]
  );

  const deleteTask = useCallback(
    async (id) => {
      ensureListReady();

      const remainingTodos = todos.filter((todo) => todo.id !== id);
      const isInTodos = remainingTodos.length !== todos.length;
      const collectionName = isInTodos ? "todos" : "completedTodos";

      await deleteDoc(getDocRef(collectionName, id));

      if (isInTodos) {
        const runningId =
          remainingTodos.find((todo) => todo.isRunning)?.id ?? null;

        const normalized = remainingTodos.map((todo, index) => ({
          ...todo,
          order: index,
          isRunning: runningId ? todo.id === runningId : false,
        }));

        setTodos(normalized);
        await writeTodosWithRunner(normalized);
      }
    },
    [ensureListReady, getDocRef, todos, writeTodosWithRunner]
  );

  const updateProblem = useCallback(
    async (problemId, updates) => {
      ensureListReady();
      if (!problemId) return;
      await updateDoc(getDocRef("problems", problemId), updates);
    },
    [ensureListReady, getDocRef]
  );

  const startProblem = useCallback(
    async (taskId, options = {}) => {
      ensureListReady();
      if (!taskId) {
        throw new Error("Task ID is required to start a problem.");
      }

      const problemsRef = getCollectionRef("problems");
      const problemRef = doc(problemsRef);

      let rawProblemUrl = options.rawProblemUrl || "";
      if (options.rawProblemFile) {
        const withTimeout = (promise, ms) =>
          new Promise((resolve, reject) => {
            const timer = setTimeout(
              () => reject(new Error("Storage upload timed out")),
              ms
            );
            promise
              .then((value) => {
                clearTimeout(timer);
                resolve(value);
              })
              .catch((error) => {
                clearTimeout(timer);
                reject(error);
              });
          });

        try {
          rawProblemUrl =
            (await withTimeout(
              uploadFileToStorage(
                options.rawProblemFile,
                `users/${user?.uid}/todoLists/${activeListId}/problems/${problemRef.id}/raw-${Date.now()}`
              ),
              12000
            )) || rawProblemUrl;
        } catch (error) {
          console.warn(
            "Failed to upload raw problem image; using inline fallback if available.",
            error
          );
        }
      }

      const attachments =
        Array.isArray(options.attachments) && options.attachments.length
          ? options.attachments
          : [];

      const normalizedProblemType = normalizeProblemType(
        options.problemType || options.metadata?.problemType || ""
      );

      const baseTimestamp = Date.now();
      const rawProblemCandidate = rawProblemUrl || options.rawProblem || "";
      const isImageLike =
        !!options.rawProblemFile ||
        (typeof rawProblemCandidate === "string" &&
          (rawProblemCandidate.startsWith("http") ||
            rawProblemCandidate.startsWith("data:image/")));
      const inputType = isImageLike
        ? "image"
        : rawProblemCandidate
        ? "text"
        : "manual";

      let attemptId = null;
      let attemptState = null;
      let attemptHintLevel = 0;
      try {
        let working = createAttemptSnapshot({
          taskId,
          problemId: problemRef.id,
          source: inputType,
          nowMs: baseTimestamp,
          state: AttemptStates.INTAKE,
        });

      const transitionPlan =
        inputType === "image"
          ? [AttemptEventTypes.OCR_REQUESTED, AttemptEventTypes.OCR_SUCCEEDED]
          : [AttemptEventTypes.GATE_OPENED];

        const transitions = [];
        let nowCursor = baseTimestamp;
        transitionPlan.forEach((eventType) => {
          const result = applyAttemptEvent(working, {
            type: eventType,
            nowMs: nowCursor,
          });
          nowCursor += 1;
          if (result.ok) {
            transitions.push(result);
            working = result.attempt;
          }
        });

        attemptState = working.state;
        attemptHintLevel = working.hintLevel || 0;

        const created = await createProblemAttempt({
          db,
          userId: user.uid,
          listId: activeListId,
          taskId,
          problemId: problemRef.id,
          source: inputType,
          state: attemptState,
          nowMs: baseTimestamp,
          extra: {
            updatedAt: working.updatedAt,
            hintLevel: attemptHintLevel,
            problemSource: options.source || "",
          },
        });
        attemptId = created.attemptId;

        void logTelemetryEvent(
          {
            type: "attempt_created",
            attemptId,
            taskId,
            problemId: problemRef.id,
            state: attemptState,
            inputType,
            problemSource: options.source || "",
          },
          baseTimestamp
        );

        transitions.forEach((transitionResult, index) => {
          void logTelemetryEvent(
            {
              type: "attempt_fsm_transition",
              attemptId,
              taskId,
              problemId: problemRef.id,
              fsmEventType: transitionResult.type,
              stateFrom: transitionResult.from,
              stateTo: transitionResult.to,
              sequence: index,
            },
            transitionResult.nowMs
          );
        });
      } catch (error) {
        console.warn("Failed to initialize attempt FSM", error);
      }

      const payload = {
        taskId,
        listId: activeListId,
        timestamp: baseTimestamp,
        rawProblem: rawProblemUrl || options.rawProblem || "",
        problemText: options.problemText || options.rawProblem || "",
        statement: options.statement || "",
        latex: options.latex || "",
        latexVerified:
          typeof options.latexVerified === "boolean"
            ? options.latexVerified
            : inputType === "image"
            ? null
            : true,
        problemType: normalizedProblemType || "Other",
        approach: options.approach || "",
        difficultyScore: Number.isInteger(options.difficultyScore)
          ? options.difficultyScore
          : options.metadata?.difficultyScore
          ? Number(options.metadata.difficultyScore)
          : options.difficultyScore
          ? Number(options.difficultyScore)
          : null,
        whyType: options.whyType || "",
        whyApproach: options.whyApproach || "",
        hintLevelsUsed: options.hintLevelsUsed || 0,
        hintTimeline: options.hintTimeline || [],
        hintCheckResponses: options.hintCheckResponses || [],
        oneNoteUrl: options.oneNoteUrl || "",
        attachments,
        mistakeTags: options.mistakeTags || [],
        finalReflection: options.finalReflection || "",
        status: "active",
        hintLevel1: options.hintLevel1 || null,
        metadata: options.metadata || null,
        canonicalSolution: options.canonicalSolution || null,
        source: options.source || "",
        ...(attemptId
          ? {
              attemptId,
              attemptState: attemptState || AttemptStates.INTAKE,
              attemptHintLevel,
              attemptFsmVersion: ATTEMPT_FSM_VERSION,
            }
          : {}),
      };

      await setDoc(problemRef, payload);
      await updateTask(
        taskId,
        { activeProblemId: problemRef.id, isProblemSolving: true },
        "todos"
      );
      return problemRef.id;
    },
    [
      activeListId,
      ensureListReady,
      getCollectionRef,
      logTelemetryEvent,
      updateTask,
      uploadFileToStorage,
      user?.uid,
    ]
  );

  const analyzeAndCreateProblemFromText = useCallback(
    async (taskId, text) => {
      ensureListReady();
      if (!taskId || !text) {
        throw new Error("Task ID and text are required.");
      }

      try {
        const { problem: analysisResult } = await problemApi.fromText({ text });

        const problemOptions = {
          rawProblem: text,
          problemText: text,
          latex: analysisResult.latex,
          statement: analysisResult.statement || text,
          metadata: analysisResult.metadata,
          problemType: analysisResult.metadata?.problemType,
          difficultyScore: analysisResult.metadata?.difficultyScore,
          hintLevel1: analysisResult.hintLevel1,
          canonicalSolution: analysisResult.canonicalSolution,
          source: analysisResult.metadata?.source,
        };

        const problemId = await startProblem(taskId, problemOptions);
        void (async () => {
          try {
            await updateProblem(problemId, { hintsGenerating: true });
            let latestHints = [];
            const hintResponse = await problemApi.generateHintsStream(
              problemId,
              {
                problemText: problemOptions.problemText,
                problemLatex: problemOptions.latex,
                canonicalSolution: problemOptions.canonicalSolution || null,
              },
              {
                onEvent: (event) => {
                  if (event?.type !== "hint" || !event?.hint) return;
                  latestHints = [
                    ...latestHints.filter((hint) => hint.level !== event.hint.level),
                    event.hint,
                  ].sort((a, b) => (a.level || 0) - (b.level || 0));
                  void updateProblem(problemId, { hints: latestHints });
                },
              }
            );
            if (hintResponse?.hints?.length) {
              await updateProblem(problemId, {
                hints: hintResponse.hints,
                hintsGeneratedAt: Date.now(),
                hintsGenerating: false,
              });
            } else {
              await updateProblem(problemId, { hintsGenerating: false });
            }
          } catch (error) {
            console.error("Failed to generate hints for problem:", error);
            await updateProblem(problemId, { hintsGenerating: false });
          }
        })();

        return problemId;
      } catch (error) {
        console.error("Error analyzing and creating problem:", error);
        throw error;
      }
    },
    [ensureListReady, startProblem, updateProblem]
  );

  const runEnrichmentAndHints = useCallback(
    async (problemId, seed = {}) => {
      if (!problemId) return;

      const existingController = hintStreamControllersRef.current.get(problemId);
      if (existingController) {
        existingController.abort();
      }
      const controller = new AbortController();
      hintStreamControllersRef.current.set(problemId, controller);

      const rawFallback = seed.rawProblem || "";
      const isDataUrl =
        typeof rawFallback === "string" && rawFallback.startsWith("data:image/");
      const hintProblemText =
        seed.problemText ||
        (seed.latex ? `LaTeX: ${seed.latex}` : "") ||
        (isDataUrl ? "" : rawFallback);

      try {
        await updateProblem(problemId, { hintsGenerating: true });

        if (!hintProblemText && !seed.latex) {
          await updateProblem(problemId, { hintsGenerating: false });
          console.warn("Skipping hint generation: no problem text or latex available.");
          return;
        }

        let latestHints = [];
        const hintsPromise = problemApi
          .generateHintsStream(
            problemId,
            {
              problemText: hintProblemText,
              problemLatex: seed.latex,
              canonicalSolution: seed.canonicalSolution || null,
            },
            {
              signal: controller.signal,
              onEvent: (event) => {
                if (event?.type !== "hint" || !event?.hint) return;
                latestHints = [
                  ...latestHints.filter((hint) => hint.level !== event.hint.level),
                  event.hint,
                ].sort((a, b) => (a.level || 0) - (b.level || 0));
                void updateProblem(problemId, { hints: latestHints });
              },
            }
          )
          .then(async (hintResponse) => {
            if (hintResponse?.hints?.length) {
              await updateProblem(problemId, {
                hints: hintResponse.hints,
                hintsGeneratedAt: Date.now(),
                hintsGenerating: false,
              });
            } else {
              await updateProblem(problemId, { hintsGenerating: false });
            }
          })
          .catch(async (error) => {
            console.error("Failed to generate hints:", error);
            await updateProblem(problemId, { hintsGenerating: false });
          })
          .finally(() => {
            hintStreamControllersRef.current.delete(problemId);
          });

        const enrichPromise = (async () => {
          try {
            const enrichPayload = {
              problemText: seed.problemText || "",
              problemLatex: seed.latex || "",
            };
            const enrichment = await problemApi.enrichProblem(enrichPayload);
            const enrichedMetadata = enrichment?.metadata || seed.metadata || {};
            const enrichedCanonical =
              enrichment?.canonicalSolution || seed.canonicalSolution || null;
            const normalizedProblemType = normalizeProblemType(
              enrichedMetadata?.problemType || seed.problemType || "Other"
            );
            const difficultyScore =
              typeof enrichedMetadata?.difficultyScore === "number"
                ? enrichedMetadata.difficultyScore
                : seed.difficultyScore || null;

            await updateProblem(problemId, {
              metadata: enrichedMetadata,
              canonicalSolution: enrichedCanonical,
              hintLevel1: enrichment?.hintLevel1 || seed.hintLevel1 || null,
              problemType: normalizedProblemType || "Other",
              difficultyScore,
            });
          } catch (error) {
            console.error("Failed to enrich problem:", error);
          }
        })();

        await Promise.allSettled([hintsPromise, enrichPromise]);
      } catch (error) {
        console.error("Failed to enrich/generate hints:", error);
        await updateProblem(problemId, { hintsGenerating: false });
        hintStreamControllersRef.current.delete(problemId);
      }
    },
    [updateProblem]
  );

  const analyzeAndCreateProblemFromImage = useCallback(
    async (taskId, input) => {
      ensureListReady();
      if (!taskId || !input) {
        throw new Error("Task ID and image input are required.");
      }

      try {
        const isUrl = typeof input === "string";
        const { problem: analysisResult } = await problemApi.fromImage(
          isUrl ? { imageUrl: input, mode: "latex" } : { file: input, mode: "latex" }
        );
        const inlineRawProblem =
          typeof analysisResult.rawProblem === "string" &&
          analysisResult.rawProblem.length <= 900000
            ? analysisResult.rawProblem
            : "";
        const rawProblemFallback =
          inlineRawProblem || analysisResult.problemText || "";
        
        const problemOptions = {
          rawProblemFile: isUrl ? null : input,
          rawProblem: rawProblemFallback,
          problemText: analysisResult.problemText || "",
          latex: analysisResult.latex,
          statement: analysisResult.statement || "",
          metadata: analysisResult.metadata,
          problemType: analysisResult.metadata?.problemType,
          difficultyScore: analysisResult.metadata?.difficultyScore,
          hintLevel1: analysisResult.hintLevel1,
          canonicalSolution: analysisResult.canonicalSolution,
          source: analysisResult.metadata?.source,
        };

        const problemId = await startProblem(taskId, problemOptions);
        void runEnrichmentAndHints(problemId, {
          rawProblem: rawProblemFallback,
          problemText: problemOptions.problemText,
          latex: problemOptions.latex,
          metadata: problemOptions.metadata,
          problemType: problemOptions.problemType,
          difficultyScore: problemOptions.difficultyScore,
          hintLevel1: problemOptions.hintLevel1,
          canonicalSolution: problemOptions.canonicalSolution,
        });

        return problemId;

      } catch (error) {
        console.error("Error analyzing and creating problem from image:", error);
        throw error;
      }
    },
    [ensureListReady, startProblem, runEnrichmentAndHints]
  );

  const ocrLatexFromImage = useCallback(
    async (input) => {
      ensureListReady();
      if (!input) {
        throw new Error("Image input is required.");
      }
      const isUrl = typeof input === "string";
      const { problem: analysisResult } = await problemApi.fromImage(
        isUrl ? { imageUrl: input, mode: "latex" } : { file: input, mode: "latex" }
      );
      return {
        latex: analysisResult?.latex || "",
        problemText: analysisResult?.problemText || "",
      };
    },
    [ensureListReady]
  );

  const checkAnswerForProblem = useCallback(
    async (problemId, payload) => {
      ensureListReady();
      if (!problemId) {
        throw new Error("Problem ID is required.");
      }
      const studentAnswerLatex = String(payload?.studentAnswerLatex || "").trim();
      if (!studentAnswerLatex) {
        throw new Error("studentAnswerLatex is required.");
      }

      const result = await problemApi.checkAnswer({
        problemText: payload?.problemText || "",
        problemLatex: payload?.problemLatex || "",
        canonicalSolution: payload?.canonicalSolution || null,
        studentAnswerLatex,
      });

      await updateProblem(problemId, {
        studentAnswerLatex,
        answerCheck: result,
        answerCheckedAt: Date.now(),
        answerCorrect: typeof result?.correct === "boolean" ? result.correct : null,
      });

      return result;
    },
    [ensureListReady, updateProblem]
  );

  const reportLatexMismatch = useCallback(
    async (problemId) => {
      ensureListReady();
      if (!problemId) return;
      const problem = problems.find((entry) => entry.id === problemId);
      if (!problem) {
        console.warn("Problem not found for re-analysis.");
        return;
      }
      const source = problem.rawProblem || "";
      const isImageSource =
        typeof source === "string" &&
        (source.startsWith("http") || source.startsWith("data:image/"));
      if (!isImageSource) {
        console.warn("Cannot re-run analysis: no image source available.");
        return;
      }

      try {
        const { problem: analysisResult } = await problemApi.fromImage({
          imageUrl: source,
          mode: "latex",
        });
        const nextProblemText = analysisResult.problemText || problem.problemText || "";
        const nextLatex = analysisResult.latex || problem.latex || "";
        await updateProblem(problemId, {
          problemText: nextProblemText,
          latex: nextLatex,
          statement: analysisResult.statement || "",
          latexVerified: null,
          hints: [],
          hintLevelsUsed: 0,
          hintTimeline: [],
          hintsGeneratedAt: null,
          hintsGenerating: true,
        });
        void runEnrichmentAndHints(problemId, {
          rawProblem: source,
          problemText: nextProblemText,
          latex: nextLatex,
          metadata: problem.metadata,
          problemType: problem.problemType,
          difficultyScore: problem.difficultyScore,
          hintLevel1: problem.hintLevel1,
          canonicalSolution: problem.canonicalSolution,
        });
      } catch (error) {
        console.error("Failed to re-run analysis:", error);
        await updateProblem(problemId, { hintsGenerating: false });
      }
    },
    [ensureListReady, problems, runEnrichmentAndHints, updateProblem]
  );


  const answerChat = useCallback(
    async (problemId, sessionId, conversation, message, options = {}) => {
      ensureListReady();
      if (!problemId || !sessionId || !message) {
        throw new Error("Problem ID, session ID, and message are required for chat.");
      }

      const problem = problems.find(p => p.id === problemId);
      if (!problem) {
        throw new Error("Problem not found for chat.");
      }

      try {
        const { assistantMessage } = await problemApi.answerChat(sessionId, {
          conversation,
          message,
          mode: options.mode || "diagnostic",
          level: problem.hintLevelsUsed || 1,
          problemContext: {
            problemText:
              options.problemText ||
              problem.statement ||
              problem.problemText ||
              problem.rawProblem ||
              "",
            problemLatex: problem.latex || "",
            metadata: problem.metadata || {},
            attemptState: problem.attemptState || null,
            hintLevel: problem.hintLevelsUsed || 0,
          },
          canonicalSolution: problem.canonicalSolution || null,
        });
        return assistantMessage || "";
      } catch (error) {
        console.error("Error getting chat response:", error);
        throw error;
      }
    },
    [ensureListReady, problems]
  );

  const recordHint = useCallback(
    async (problemId, level, resourceId) => {
      ensureListReady();
      if (!problemId || typeof level === "undefined") return;

      const current = problems.find((problem) => problem.id === problemId);
      const maxLevel =
        typeof current?.hintLevelsUsed === "number"
          ? current.hintLevelsUsed
          : 0;
      const nowMs = Date.now();
      const nextHintLevel = Math.max(maxLevel, Number(level) || 0);

      let nextAttemptState = current?.attemptState || null;
      let nextAttemptHintLevel =
        typeof current?.attemptHintLevel === "number"
          ? current.attemptHintLevel
          : typeof current?.hintLevelsUsed === "number"
          ? current.hintLevelsUsed
          : 0;

      const attemptId = current?.attemptId || null;

      if (attemptId) {
        const attemptSnapshot = {
          ...createAttemptSnapshot({
            attemptId,
            taskId: current?.taskId || null,
            problemId,
            source: current?.source || "unknown",
            nowMs,
            state: current?.attemptState || AttemptStates.ATTEMPT_ACTIVE,
          }),
          hintLevel: nextAttemptHintLevel,
        };

        const transition = applyAttemptEvent(attemptSnapshot, {
          type: AttemptEventTypes.HINT_UNLOCKED,
          level: nextHintLevel,
          nowMs,
        });

        if (transition.ok) {
          nextAttemptState = transition.attempt.state;
          nextAttemptHintLevel = transition.attempt.hintLevel || nextAttemptHintLevel;
          void logTelemetryEvent(
            {
              type: "attempt_fsm_transition",
              attemptId,
              taskId: current?.taskId || null,
              problemId,
              fsmEventType: AttemptEventTypes.HINT_UNLOCKED,
              stateFrom: transition.from,
              stateTo: transition.to,
              data: { level: nextHintLevel },
            },
            nowMs
          );
          void updateProblemAttempt({
            db,
            userId: user.uid,
            listId: activeListId,
            attemptId,
            updates: {
              state: nextAttemptState,
              hintLevel: nextAttemptHintLevel,
              updatedAt: nowMs,
            },
          }).catch((error) => {
            console.warn("Failed to update attempt after hint unlock", error);
          });
        }
      }

      const payload = {
        hintLevelsUsed: Math.max(maxLevel, Number(level) || 0),
        hintTimeline: arrayUnion({
          level,
          resourceId: resourceId || null,
          timestamp: Date.now(),
        }),
        ...(attemptId
          ? {
              attemptState: nextAttemptState || current?.attemptState || AttemptStates.ATTEMPT_ACTIVE,
              attemptHintLevel: nextAttemptHintLevel,
              attemptFsmVersion:
                typeof current?.attemptFsmVersion === "number"
                  ? current.attemptFsmVersion
                  : ATTEMPT_FSM_VERSION,
            }
          : {}),
      };

      await updateDoc(getDocRef("problems", problemId), payload);
    },
    [activeListId, ensureListReady, getDocRef, logTelemetryEvent, problems, user?.uid]
  );

  const requestHint = useCallback(
    async (problemId, level, request = {}) => {
      ensureListReady();
      if (!problemId || typeof level === "undefined") return;

      const current = problems.find((entry) => entry.id === problemId) || null;
      const problemText =
        request.problemText ||
        current?.statement ||
        current?.problemText ||
        current?.rawProblem ||
        "";

      const payload = {
        level,
        problemText,
        problemLatex: current?.latex || "",
        studentResponse: request.response || "",
        responseType: request.responseType || "text",
        planText: request.planText || "",
        canonicalSolution: current?.canonicalSolution || null,
      };

      const prefetchOnly = !!request.prefetchOnly;

      let nextHint = null;
      const result = await problemApi.adaptHintForLevel(problemId, payload);
      nextHint = result?.hint || null;

      if (nextHint) {
        const existingHints = Array.isArray(current?.hints) ? current.hints : [];
        const updatedHints = [
          ...existingHints.filter((hint) => hint.level !== nextHint.level),
          nextHint,
        ].sort((a, b) => (a.level || 0) - (b.level || 0));

        await updateProblem(problemId, {
          hints: updatedHints,
          hintsGeneratedAt: Date.now(),
        });
      }

      if (!prefetchOnly) {
        await recordHint(problemId, level, nextHint?.resourceId || null);
      }
      return nextHint;
    },
    [ensureListReady, problems, recordHint, updateProblem]
  );

  const requestDesmosExpressions = useCallback(
    async (problemId) => {
      ensureListReady();
      if (!problemId) return;

      const current = problems.find((entry) => entry.id === problemId) || null;
      if (!current) return;

      const problemText =
        current?.statement ||
        current?.problemText ||
        current?.rawProblem ||
        "";

      const result = await problemApi.suggestDesmosExpressions(problemId, {
        problemText,
        latex: current?.latex || "",
      });

      const expressions = Array.isArray(result?.expressions)
        ? result.expressions
        : [];

      if (expressions.length) {
        await updateProblem(problemId, {
          desmosExpressions: expressions,
          desmosGeneratedAt: Date.now(),
        });
      }

      return expressions;
    },
    [ensureListReady, problems, updateProblem]
  );

  const recordHintCheckResponse = useCallback(
    async (problemId, response = {}) => {
      ensureListReady();
      if (!problemId) return;

      const level = Number(response.level);
      if (!Number.isFinite(level)) return;

      const responseType =
        response.responseType === "dont_know" ? "dont_know" : "text";
      const responseText =
        responseType === "dont_know" ? "" : String(response.response || "").trim();
      if (responseType === "text" && !responseText) return;

      const nowMs = Date.now();
      const current = problems.find((entry) => entry.id === problemId) || null;

      await updateDoc(getDocRef("problems", problemId), {
        hintCheckResponses: arrayUnion({
          level,
          response: responseText,
          responseType,
          timestamp: nowMs,
        }),
      });

      void logTelemetryEvent(
        {
          type: "hint_check_submitted",
          attemptId: current?.attemptId || null,
          taskId: current?.taskId || null,
          problemId,
          level,
          responseType,
          response: responseText,
        },
        nowMs
      );

      const nextLevel = level + 1;
      if (nextLevel <= HINT_LEVELS.length && current) {
        const alreadyUnlocked =
          Array.isArray(current.hintTimeline) &&
          current.hintTimeline.some(
            (entry) => Number(entry?.level) === nextLevel
          );
        if (!alreadyUnlocked) {
          const textCandidate = String(current.problemText || "").trim();
          const rawProblem =
            typeof current.rawProblem === "string" ? current.rawProblem : "";
          const rawIsImage =
            rawProblem.startsWith("http") || rawProblem.startsWith("data:image/");
          const fallbackText =
            textCandidate ||
            (current.latex ? `LaTeX: ${current.latex}` : "") ||
            (!rawIsImage ? rawProblem : "");

          if (fallbackText) {
            const hintList = Array.isArray(current.hints) ? current.hints : [];
            let priorHints = hintList
              .filter(
                (hint) =>
                  Number.isFinite(Number(hint?.level)) &&
                  Number(hint.level) <= level &&
                  hint?.text
              )
              .map((hint) => ({ level: hint.level, text: hint.text }));
            if (
              current.hintLevel1?.text &&
              !priorHints.some((hint) => Number(hint.level) === 1)
            ) {
              priorHints = [
                ...priorHints,
                { level: 1, text: current.hintLevel1.text },
              ];
            }

            try {
              const adapted = await problemApi.adaptHintForLevel(problemId, {
                level: nextLevel,
                problemText: fallbackText,
                problemLatex: current.latex || "",
                canonicalSolution: current.canonicalSolution || null,
                metadata: current.metadata || {},
                studentResponse:
                  responseText ||
                  (responseType === "dont_know" ? "I don't know." : ""),
                responseType,
                priorHints,
              });
              const updatedHint = adapted?.hint;
              if (updatedHint?.text) {
                const updatedHints = [
                  ...hintList.filter(
                    (hint) => Number(hint?.level) !== nextLevel
                  ),
                  { ...updatedHint, level: nextLevel },
                ]
                  .filter((hint) => hint?.text)
                  .sort((a, b) => (a.level || 0) - (b.level || 0));
                await updateProblem(problemId, { hints: updatedHints });
              }
            } catch (error) {
              console.warn("Failed to adapt next hint", error);
            }
          }
        }
      }
    },
    [ensureListReady, getDocRef, logTelemetryEvent, problems, updateProblem]
  );

  const submitAttemptPlan = useCallback(
    async (problemId, plan = {}) => {
      ensureListReady();
      if (!problemId) return;

      const current = problems.find((entry) => entry.id === problemId) || null;
      const nowMs = Date.now();
      const mode = plan?.mode === "dont_know" ? "dont_know" : "plan";

      const payload = {
        planText: plan.planText || "",
        planMethodGuess: plan.methodGuess || "",
        planWhy: plan.why || "",
        planFirstMove: plan.firstMove || "",
        planDontKnowReason: plan.dontKnowReason || "",
        planSubmissionType: mode,
        planSubmittedAt: nowMs,
      };

      const attemptId = current?.attemptId || null;
      const attemptState = current?.attemptState || null;
      if (
        attemptId &&
        [AttemptStates.GATE_OPEN, AttemptStates.OCR_PENDING, AttemptStates.INTAKE].includes(
          attemptState
        )
      ) {
        const attemptSnapshot = {
          ...createAttemptSnapshot({
            attemptId,
            taskId: current?.taskId || null,
            problemId,
            source: current?.source || "unknown",
            nowMs,
            state: attemptState,
          }),
          hintLevel:
            typeof current?.attemptHintLevel === "number"
              ? current.attemptHintLevel
              : typeof current?.hintLevelsUsed === "number"
              ? current.hintLevelsUsed
              : 0,
        };
        const transition = applyAttemptEvent(attemptSnapshot, {
          type: AttemptEventTypes.ATTEMPT_STARTED,
          nowMs,
        });
        if (transition.ok) {
          payload.attemptState = transition.attempt.state;
          payload.attemptHintLevel = transition.attempt.hintLevel || 0;
          payload.attemptFsmVersion =
            typeof current?.attemptFsmVersion === "number"
              ? current.attemptFsmVersion
              : ATTEMPT_FSM_VERSION;

          void logTelemetryEvent(
            {
              type: "attempt_fsm_transition",
              attemptId,
              taskId: current?.taskId || null,
              problemId,
              fsmEventType: AttemptEventTypes.ATTEMPT_STARTED,
              stateFrom: transition.from,
              stateTo: transition.to,
            },
            nowMs
          );

          void updateProblemAttempt({
            db,
            userId: user.uid,
            listId: activeListId,
            attemptId,
            updates: {
              state: transition.attempt.state,
              hintLevel: transition.attempt.hintLevel || 0,
              updatedAt: nowMs,
            },
          }).catch((error) => {
            console.warn("Failed to update attempt on plan submission", error);
          });
        }
      }

      void logTelemetryEvent(
        {
          type: mode === "dont_know" ? "attempt_gate_skipped" : "attempt_plan_submitted",
          attemptId,
          taskId: current?.taskId || null,
          problemId,
          planText: payload.planText,
          methodGuess: payload.planMethodGuess,
          why: payload.planWhy,
          firstMove: payload.planFirstMove,
          dontKnowReason: payload.planDontKnowReason,
        },
        nowMs
      );

      await updateProblem(problemId, payload);
    },
    [
      activeListId,
      ensureListReady,
      logTelemetryEvent,
      problems,
      updateProblem,
      user?.uid,
    ]
  );

  const recordAttachment = useCallback(
    async (problemId, attachment) => {
      ensureListReady();
      if (!problemId || !attachment) return;

      let url = attachment.url || "";
      if (attachment.file) {
        url =
          (await uploadFileToStorage(
            attachment.file,
            `users/${user?.uid}/todoLists/${activeListId}/problems/${problemId}/attachments/${Date.now()}_${
              attachment.file.name || "attachment"
            }`
          )) || url;
      }

      if (!url) return;

      await updateDoc(getDocRef("problems", problemId), {
        attachments: arrayUnion({
          url,
          timestamp: Date.now(),
          name: attachment.name || attachment.file?.name || "Attachment",
        }),
      });
    },
    [activeListId, ensureListReady, getDocRef, uploadFileToStorage, user?.uid]
  );

  const toggleTaskCompletion = useCallback(
    async (id, forceComplete) => {
      ensureListReady();

      const sourceTask =
        todos.find((todo) => todo.id === id) ||
        completedTodos.find((todo) => todo.id === id);

      if (!sourceTask) {
        throw new Error("Task not found");
      }

      const shouldComplete =
        typeof forceComplete === "boolean"
          ? forceComplete
          : !sourceTask.complete;

      const sourceCollection = sourceTask.complete
        ? "completedTodos"
        : "todos";
      const targetCollection = shouldComplete ? "completedTodos" : "todos";

      const batch = writeBatch(db);
      const targetTask = {
        ...sourceTask,
        complete: shouldComplete,
        completed: shouldComplete,
        isRunning: false,
        order: shouldComplete ? null : todos.length,
      };

      batch.delete(getDocRef(sourceCollection, id));
      batch.set(getDocRef(targetCollection, id), targetTask);

      if (sourceCollection === "todos") {
        const remaining = todos
          .filter((todo) => todo.id !== id)
          .map((todo, index) => ({
            id: todo.id,
            order: index,
          }));

        remaining.forEach(({ id: todoId, order }) => {
          batch.update(getDocRef("todos", todoId), { order });
        });
      }

      await batch.commit();
    },
    [ensureListReady, completedTodos, getDocRef, todos]
  );

  const finishProblem = useCallback(
    async (problemId, options = {}) => {
      ensureListReady();
      if (!problemId) return;

      const nowMs = Date.now();
      const currentProblem =
        problems.find((entry) => entry.id === problemId) || null;
      const targetTaskId =
        options.taskId || currentProblem?.taskId;
      const completeTask =
        typeof options.completeTask === "boolean"
          ? options.completeTask
          : typeof options.autoComplete === "boolean"
          ? options.autoComplete
          : false;

      const payload = {
        status: "solved",
        completedAt: nowMs,
      };

      if (typeof options.finalReflection === "string") {
        payload.finalReflection = options.finalReflection;
      }
      if (typeof options.confidence !== "undefined") {
        payload.confidence = options.confidence;
      }
      if (typeof options.commitAnswer === "string") {
        payload.commitAnswer = options.commitAnswer;
      }
      if (typeof options.commitReasoning === "string") {
        payload.commitReasoning = options.commitReasoning;
      }
      if (typeof options.commitConfidence === "number") {
        payload.commitConfidence = options.commitConfidence;
      }
      if (typeof options.commitSubmittedAt === "number") {
        payload.commitSubmittedAt = options.commitSubmittedAt;
      }
      if (Array.isArray(options.mistakeTags)) {
        payload.mistakeTags = options.mistakeTags;
      }
      if (typeof options.hintLevelsUsed === "number") {
        payload.hintLevelsUsed = options.hintLevelsUsed;
      }
      if (options.oneNoteUrl) {
        payload.oneNoteUrl = options.oneNoteUrl;
      }
      if (options.whyApproach) {
        payload.whyApproach = options.whyApproach;
      }
      if (options.whyType) {
        payload.whyType = options.whyType;
      }
      if (typeof options.reviewOpen === "boolean") {
        payload.reviewOpen = options.reviewOpen;
      }

      const attemptId = currentProblem?.attemptId || null;
      if (attemptId) {
        const attemptSnapshot = {
          ...createAttemptSnapshot({
            attemptId,
            taskId: currentProblem?.taskId || targetTaskId || null,
            problemId,
            source: currentProblem?.source || "unknown",
            nowMs,
            state: currentProblem?.attemptState || AttemptStates.ATTEMPT_ACTIVE,
          }),
          hintLevel:
            typeof currentProblem?.attemptHintLevel === "number"
              ? currentProblem.attemptHintLevel
              : typeof currentProblem?.hintLevelsUsed === "number"
              ? currentProblem.hintLevelsUsed
              : 0,
        };
        const transition = applyAttemptEvent(attemptSnapshot, {
          type: AttemptEventTypes.COMPLETED,
          nowMs,
        });
        if (transition.ok) {
          payload.attemptState = transition.attempt.state;
          payload.attemptHintLevel = transition.attempt.hintLevel || 0;
          payload.attemptFsmVersion =
            typeof currentProblem?.attemptFsmVersion === "number"
              ? currentProblem.attemptFsmVersion
              : ATTEMPT_FSM_VERSION;

          void logTelemetryEvent(
            {
              type: "attempt_fsm_transition",
              attemptId,
              taskId: currentProblem?.taskId || targetTaskId || null,
              problemId,
              fsmEventType: AttemptEventTypes.COMPLETED,
              stateFrom: transition.from,
              stateTo: transition.to,
            },
            nowMs
          );

          void updateProblemAttempt({
            db,
            userId: user.uid,
            listId: activeListId,
            attemptId,
            updates: {
              state: transition.attempt.state,
              hintLevel: transition.attempt.hintLevel || 0,
              updatedAt: nowMs,
              completedAt: nowMs,
            },
          }).catch((error) => {
            console.warn("Failed to update attempt on completion", error);
          });
        }
      }

      const answerCheck = options.answerCheck || null;
      const answerCorrect =
        typeof options.answerCorrect === "boolean"
          ? options.answerCorrect
          : typeof answerCheck?.correct === "boolean"
          ? answerCheck.correct
          : null;
      const answerConfidence =
        typeof answerCheck?.confidence === "number" ? answerCheck.confidence : null;

      await updateProblem(problemId, payload);
      const keepActiveProblem =
        typeof options.keepActiveProblem === "boolean" ? options.keepActiveProblem : false;
      if (targetTaskId && !keepActiveProblem) {
        await updateTask(targetTaskId, { activeProblemId: null });
      }

      if (completeTask && targetTaskId) {
        await toggleTaskCompletion(targetTaskId, true);
      }

      if (typeof options.commitConfidence === "number") {
        void logTelemetryEvent(
          {
            type: "commit_submitted",
            attemptId: attemptId || null,
            taskId: currentProblem?.taskId || targetTaskId || null,
            problemId,
            commitConfidence: options.commitConfidence,
            answerCorrect,
            answerConfidence,
          },
          nowMs
        );
      }
    },
    [
      activeListId,
      ensureListReady,
      logTelemetryEvent,
      problems,
      toggleTaskCompletion,
      updateProblem,
      updateTask,
      user?.uid,
    ]
  );

  const closeProblemReview = useCallback(
    async (problemId, taskIdOverride) => {
      ensureListReady();
      if (!problemId) return;
      const current = problems.find((entry) => entry.id === problemId) || null;
      const targetTaskId = taskIdOverride || current?.taskId || null;
      await updateProblem(problemId, { reviewOpen: false });
      if (targetTaskId) {
        await updateTask(targetTaskId, { activeProblemId: null });
      }
    },
    [ensureListReady, problems, updateProblem, updateTask]
  );

  const reorderTodos = useCallback(
    async (nextTodos) => {
      ensureListReady();

      const reordered = nextTodos.map((todo, index) => ({
        ...todo,
        order: index,
      }));

      setTodos(reordered);
      await writeTodosWithRunner(reordered);
    },
    [ensureListReady, writeTodosWithRunner]
  );

  const startTask = useCallback(
    async (taskId, options = {}) => {
      ensureListReady();

      const baseTodos = Array.isArray(options.baseTodos)
        ? options.baseTodos
        : todos;

      if (!Array.isArray(baseTodos) || !baseTodos.length) {
        return null;
      }

      const workingTodos = sortTasks(
        baseTodos.filter((todo) => !todo.complete)
      );

      const targetIndex = workingTodos.findIndex((todo) => todo.id === taskId);
      if (targetIndex === -1) {
        return null;
      }

      const [target] = workingTodos.splice(targetIndex, 1);
      if (!target) {
        return null;
      }

      const nextTodos = [
        { ...target, isRunning: true },
        ...workingTodos.map((todo) => ({ ...todo, isRunning: false })),
      ].map((todo, index) => ({ ...todo, order: index }));

      setTodos(nextTodos);
      await writeTodosWithRunner(nextTodos);
      return nextTodos[0]?.id ?? null;
    },
    [ensureListReady, todos, writeTodosWithRunner]
  );

  const pauseTask = useCallback(
    async (taskId) => {
      ensureListReady();

      const nextTodos = todos.map((todo, index) => ({
        ...todo,
        order: index,
        isRunning: todo.id === taskId ? false : todo.isRunning,
      }));

      setTodos(nextTodos);
      await writeTodosWithRunner(nextTodos);
    },
    [ensureListReady, todos, writeTodosWithRunner]
  );

  const startNextTask = useCallback(
    async (excludeId) => {
      ensureListReady();

      const baseTodos = todos.filter(
        (todo) => todo.id !== excludeId && !todo.complete
      );

      if (!baseTodos.length) {
        const hadRunner = todos.some((todo) => todo.isRunning);
        if (hadRunner) {
          const cleared = todos.map((todo, index) => ({
            ...todo,
            order: index,
            isRunning: false,
          }));
          setTodos(cleared);
          await writeTodosWithRunner(cleared);
        }
        return null;
      }

      const ordered = sortTasks(baseTodos);
      const next = ordered[0];
      if (!next) {
        return null;
      }

      return startTask(next.id, { baseTodos: ordered });
    },
    [ensureListReady, startTask, todos, writeTodosWithRunner]
  );

  const createList = useCallback(
    async (listId) => {
      ensureUser();
      const normalized = normalizeDateId(listId) || listId?.trim?.();
      if (!normalized) return;

      const docRef = doc(db, `users/${user.uid}/todoLists/${normalized}`);
      await setDoc(docRef, { createdAt: Date.now() });
      setActiveListId(docRef.id);
      setSelectedDateState(normalizeDateId(docRef.id) || docRef.id);
      return docRef.id;
    },
    [ensureUser, user?.uid]
  );

  const ensureListForDate = useCallback(
    async (dateId) => {
      ensureUser();
      const normalized = normalizeDateId(dateId);
      if (!normalized) return null;

      const existing = lists.find((list) => list.id === normalized);
      if (existing) {
        setActiveListId(normalized);
        setSelectedDateState(normalized);
        return normalized;
      }

      return createList(normalized);
    },
    [ensureUser, lists, createList]
  );

  const setSelectedDate = useCallback(
    (value, options = {}) => {
      const normalized = normalizeDateId(value);
      if (!normalized) return;

      setSelectedDateState(normalized);
      const listExists = lists.some((list) => list.id === normalized);

      if (listExists) {
        setActiveListId(normalized);
        return normalized;
      }

      if (options.ensureList) {
        return createList(normalized);
      }

      setActiveListId(null);
      return normalized;
    },
    [lists, createList]
  );

  const deleteList = useCallback(
    async (listId) => {
      ensureUser();
      if (!listId) return;

      const listRef = doc(db, `users/${user.uid}/todoLists/${listId}`);

      await deleteDoc(listRef);

      if (activeListId === listId) {
        setActiveListId(null);
      }
    },
    [activeListId, ensureUser, user?.uid]
  );

  const activeProblem = useMemo(() => {
    const active = problems
      .filter((problem) => problem?.status === "active")
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return active[0] || null;
  }, [problems]);

  const problemsByTask = useMemo(() => {
    const grouped = problems.reduce((acc, problem) => {
      if (!problem?.taskId) return acc;
      if (!acc[problem.taskId]) {
        acc[problem.taskId] = [];
      }
      acc[problem.taskId].push(problem);
      return acc;
    }, {});

    Object.keys(grouped).forEach((taskId) => {
      grouped[taskId].sort(
        (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
      );
    });

    return grouped;
  }, [problems]);

  const latestProblemByTask = useMemo(() => {
    const latest = {};
    Object.entries(problemsByTask).forEach(([taskId, entries]) => {
      if (entries?.length) {
        latest[taskId] = entries[0];
      }
    });
    return latest;
  }, [problemsByTask]);

  const userDisplayName = useMemo(() => {
    if (userProfile?.name) return userProfile.name;
    if (user?.displayName) return user.displayName;
    if (user?.email) {
      const [localPart] = user.email.split("@");
      return localPart || user.email;
    }
    return "";
  }, [userProfile, user]);

  const value = useMemo(
    () => ({
      user,
      userProfile,
      userDisplayName,
      lists,
      activeListId,
      setActiveListId,
      selectedDate,
      setSelectedDate,
      ensureListForDate,
      listSummaries,
      formatDateId,
      telemetryEnabled,
      logTelemetryEvent,
      todos,
      completedTodos,
      loading,
      problems,
      problemsByTask,
      latestProblemByTask,
      activeProblem,
      addTask,
      updateTask,
      toggleTaskCompletion,
      reorderTodos,
      startTask,
      pauseTask,
      startNextTask,
      deleteTask,
      createList,
      deleteList,
      startProblem,
      analyzeAndCreateProblemFromText,
      analyzeAndCreateProblemFromImage,
      ocrLatexFromImage,
      checkAnswerForProblem,
      reportLatexMismatch,
      answerChat,
      recordHint,
      requestHint,
      requestDesmosExpressions,
      recordHintCheckResponse,
      submitAttemptPlan,
      recordAttachment,
      finishProblem,
      closeProblemReview,
      updateProblem,
      createFlashcard,
      getDueFlashcards,
      reviewFlashcard,
      getFlashcards,
      updateFlashcard,
      deleteFlashcard,
    }),
    [
      user,
      userProfile,
      userDisplayName,
      lists,
      activeListId,
      selectedDate,
      setSelectedDate,
      ensureListForDate,
      listSummaries,
      telemetryEnabled,
      logTelemetryEvent,
      todos,
      completedTodos,
      loading,
      problems,
      problemsByTask,
      latestProblemByTask,
      activeProblem,
      addTask,
      updateTask,
      toggleTaskCompletion,
      reorderTodos,
      startTask,
      pauseTask,
      startNextTask,
      deleteTask,
      createList,
      deleteList,
      startProblem,
      analyzeAndCreateProblemFromText,
      analyzeAndCreateProblemFromImage,
      ocrLatexFromImage,
      checkAnswerForProblem,
      reportLatexMismatch,
      answerChat,
      recordHint,
      requestHint,
      requestDesmosExpressions,
      recordHintCheckResponse,
      submitAttemptPlan,
      recordAttachment,
      finishProblem,
      closeProblemReview,
      updateProblem,
      createFlashcard,
      getDueFlashcards,
      reviewFlashcard,
      getFlashcards,
      updateFlashcard,
      deleteFlashcard,
    ]
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTasks = () => {
  const context = useContext(TaskContext);

  if (!context) {
    throw new Error("useTasks must be used within a TaskProvider");
  }

  return context;
};
